/**
 * Shared utilities for connection management, session state, and WebSocket failure handling.
 */

// Session storage keys for runtime BOSH preference
export const RUNTIME_PREFER_BOSH_SESSION_KEY = 'xmpp_prefer_bosh_runtime';
export const RUNTIME_PREFER_BOSH_SET_AT_KEY = 'xmpp_prefer_bosh_runtime_set_at';
export const RUNTIME_PREFER_BOSH_LOCK_KEY = 'xmpp_prefer_bosh_session_lock';

// Session storage key for WebSocket failure count
export const RUNTIME_WS_FAILURE_COUNT_KEY = 'xmpp_ws_failure_count';

// Session storage keys recording that this network needs a TURN relay for media.
export const RUNTIME_FORCE_RELAY_SESSION_KEY = 'media_force_relay_runtime';
export const RUNTIME_FORCE_RELAY_SET_AT_KEY = 'media_force_relay_runtime_set_at';

/**
 * How long a learned "this network needs TURN relay" signal stays in effect.
 *
 * Relay-only ICE costs an extra hop and TURN capacity, so it should not be sticky forever, but it must
 * outlive the reconnect that follows an ICE failure - otherwise the retry is made with the same
 * all-candidates policy that just failed and the participant loops.
 */
export const DEFAULT_RUNTIME_FORCE_RELAY_TTL_MS = 30 * 60 * 1000;

/**
 * Whether the peer connections built during THIS page load were given iceTransportPolicy=relay.
 *
 * Deliberately module state rather than sessionStorage. The persisted flag above answers "does this
 * network need a relay", which is a lesson meant to survive the page reload that the reconnect path
 * performs. This answers a different and narrower question - "was the RTCPeerConnection that just failed
 * actually constructed with relay" - and the answer resets with the page, because iceTransportPolicy is
 * fixed at construction. Conflating the two lets a relay need recorded mid-session (a closed bridge data
 * channel, say, which never rebuilds the peer connection) be mistaken for a relay attempt that was made
 * and failed.
 */
let _mediaRelayPolicyApplied = false;

/**
 * Records the media transport policy that was applied when building this page load's conference options.
 *
 * @param {boolean} applied - Whether iceTransportPolicy=relay was used.
 * @returns {void}
 */
export function setMediaRelayPolicyApplied(applied) {
    _mediaRelayPolicyApplied = Boolean(applied);
}

/**
 * Tells whether this page load's peer connections were built with iceTransportPolicy=relay.
 *
 * Use this - not {@link readRuntimeForceRelayFlag} - to decide whether a relay-only path has actually
 * been tried and failed.
 *
 * @returns {boolean}
 */
export function wasMediaRelayPolicyApplied() {
    return _mediaRelayPolicyApplied;
}

/**
 * Records that media could not flow without a TURN relay on this network.
 *
 * Called when ICE fails or the bridge data channel closes. The flag is read when the NEXT peer
 * connection is built, because iceTransportPolicy is fixed at RTCPeerConnection construction and cannot
 * be changed on a live connection.
 *
 * @param {Object} logger - Logger instance.
 * @param {string} [reason] - Human-readable reason, for logs.
 * @returns {void}
 */
export function recordMediaRelayRequired(logger, reason = 'ice-failure') {
    try {
        if (window.sessionStorage?.getItem(RUNTIME_FORCE_RELAY_SESSION_KEY) !== '1') {
            logger?.warn(`Media relay required on this network (${reason}); `
                + 'subsequent peer connections will use iceTransportPolicy=relay.');
        }
        window.sessionStorage?.setItem(RUNTIME_FORCE_RELAY_SESSION_KEY, '1');
        window.sessionStorage?.setItem(RUNTIME_FORCE_RELAY_SET_AT_KEY, String(Date.now()));
    } catch (error) {
        logger?.warn('Failed to persist media relay preference.', error);
    }
}

/**
 * Reads the learned "this network needs TURN relay" flag, expiring it after the TTL.
 *
 * @param {Object} config - The global config object.
 * @param {Object} logger - Logger instance.
 * @returns {boolean}
 */
export function readRuntimeForceRelayFlag(config, logger) {
    try {
        if (window.sessionStorage?.getItem(RUNTIME_FORCE_RELAY_SESSION_KEY) !== '1') {
            return false;
        }

        const configuredTtl = Number(config?.runtimeForceRelayTtlMs);
        const ttlMs = Number.isFinite(configuredTtl) && configuredTtl > 0
            ? configuredTtl
            : DEFAULT_RUNTIME_FORCE_RELAY_TTL_MS;
        const setAt = Number(window.sessionStorage?.getItem(RUNTIME_FORCE_RELAY_SET_AT_KEY));

        if (!Number.isFinite(setAt) || setAt <= 0) {
            window.sessionStorage?.setItem(RUNTIME_FORCE_RELAY_SET_AT_KEY, String(Date.now()));

            return true;
        }

        if (Date.now() - setAt >= ttlMs) {
            window.sessionStorage?.removeItem(RUNTIME_FORCE_RELAY_SESSION_KEY);
            window.sessionStorage?.removeItem(RUNTIME_FORCE_RELAY_SET_AT_KEY);
            logger?.info('Media relay preference expired; direct ICE candidates are re-enabled.');

            return false;
        }

        return true;
    } catch (error) {
        logger?.warn('Failed to read media relay preference.', error);

        return false;
    }
}

// Default constants
export const DEFAULT_RUNTIME_PREFER_BOSH_TTL_MS = 10 * 60 * 1000;
export const DEFAULT_RUNTIME_PREFER_BOSH_MAX_WS_FAILURES = 3;

/**
 * How many websocket failures must be observed before the session sticks to BOSH.
 *
 * A single failure triggers a one-shot BOSH retry for the current join attempt only. Sticking the whole session to
 * BOSH after one failure punished users whose websocket was merely slow to establish (high-RTT mobile links), and
 * BOSH long-polling is markedly less reliable across NAT/proxy idle timeouts than a websocket.
 */
export const DEFAULT_RUNTIME_PREFER_BOSH_MIN_WS_FAILURES = 2;

/**
 * How long to wait for a websocket to establish before retrying over BOSH.
 *
 * Establishing a websocket is DNS + TCP + TLS + HTTP upgrade + XMPP stream open + SASL + resource bind. On a
 * 300-600ms RTT link that legitimately exceeds several seconds, so a short budget here misclassifies healthy
 * connections as broken.
 */
export const DEFAULT_RUNTIME_WEBSOCKET_FALLBACK_TIMEOUT_MS = 15 * 1000;

let _runtimeRefreshResetHandled = false;

/**
 * Clears runtime BOSH preference state from session storage.
 *
 * @returns {void}
 */
function _clearRuntimePreferBoshState() {
    window.sessionStorage?.removeItem(RUNTIME_PREFER_BOSH_SESSION_KEY);
    window.sessionStorage?.removeItem(RUNTIME_PREFER_BOSH_SET_AT_KEY);
    window.sessionStorage?.removeItem(RUNTIME_PREFER_BOSH_LOCK_KEY);
    window.sessionStorage?.removeItem(RUNTIME_WS_FAILURE_COUNT_KEY);
}

/**
 * On browser refresh, reset runtime BOSH state once so WebSocket is retried first.
 *
 * @param {Object} config - The global config object.
 * @param {Object} logger - Logger instance.
 * @returns {void}
 */
function _maybeResetRuntimePreferBoshOnRefresh(config, logger) {
    if (_runtimeRefreshResetHandled) {
        return;
    }

    _runtimeRefreshResetHandled = true;

    const shouldRetryWsOnRefresh = config?.retryWebSocketOnPageRefresh !== false;

    if (!shouldRetryWsOnRefresh) {
        return;
    }

    try {
        const navigationEntry = window.performance?.getEntriesByType?.('navigation')?.[0];
        const navigationType = navigationEntry?.type;
        const isReload = navigationType === 'reload';

        if (isReload) {
            _clearRuntimePreferBoshState();
            logger?.info('Browser refresh detected; resetting runtime BOSH preference to retry WebSocket first.');
        }
    } catch (error) {
        logger?.warn('Failed to evaluate refresh-based runtime BOSH reset.', error);
    }
}

/**
 * Appends room name as a query parameter to a service URL.
 *
 * @param {string} baseUrl - BOSH or WebSocket URL.
 * @param {string} roomName - The room name.
 * @returns {string}
 */
export function appendRoomParam(baseUrl, roomName) {
    if (!baseUrl) {
        return baseUrl;
    }

    const separator = baseUrl.includes('?') ? '&' : '?';

    return `${baseUrl}${separator}room=${encodeURIComponent(roomName ?? '')}`;
}

/**
 * Reads the runtime BOSH preference from the session and auto-expires it.
 *
 * @param {Object} config - The global config object.
 * @param {Object} logger - Logger instance.
 * @returns {boolean}
 */
export function readRuntimePreferBoshFlag(config, logger) {
    try {
        _maybeResetRuntimePreferBoshOnRefresh(config, logger);

        const isLocked = window.sessionStorage?.getItem(RUNTIME_PREFER_BOSH_LOCK_KEY) === '1';
        const preferBosh = isLocked
            || window.sessionStorage?.getItem(RUNTIME_PREFER_BOSH_SESSION_KEY) === '1';

        if (!preferBosh) {
            return false;
        }

        const configuredTtl = Number(config?.runtimePreferBoshTtlMs);
        const ttlMs = Number.isFinite(configuredTtl) && configuredTtl > 0
            ? configuredTtl
            : DEFAULT_RUNTIME_PREFER_BOSH_TTL_MS;
        const setAtRaw = window.sessionStorage?.getItem(RUNTIME_PREFER_BOSH_SET_AT_KEY);
        const setAt = Number(setAtRaw);

        if (!Number.isFinite(setAt) || setAt <= 0) {
            window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SESSION_KEY, '1');
            window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SET_AT_KEY, String(Date.now()));

            return true;
        }

        // The TTL applies to the lock as well. This check used to sit behind an early return for the lock, so once
        // the failure threshold was hit the tab stayed on BOSH for its entire lifetime with no way back to
        // websocket - a single bad minute cost the user the whole meeting.
        if (Date.now() - setAt >= ttlMs) {
            _clearRuntimePreferBoshState();
            logger?.info('Runtime BOSH preference expired; websocket attempts are re-enabled.');

            return false;
        }

        window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SESSION_KEY, '1');

        return true;
    } catch (error) {
        logger?.warn('Failed to read runtime BOSH session preference.', error);

        return false;
    }
}

/**
 * Tracks websocket failures and locks the session to BOSH when threshold is reached.
 *
 * @param {Object} config - The global config object.
 * @param {Object} logger - Logger instance.
 * @returns {void}
 */
export function recordWebSocketFailure(config, logger) {
    try {
        const configuredMax = Number(config?.runtimePreferBoshMaxWsFailures);
        const maxFailures = Number.isFinite(configuredMax) && configuredMax > 0
            ? configuredMax
            : DEFAULT_RUNTIME_PREFER_BOSH_MAX_WS_FAILURES;
        const configuredMin = Number(config?.runtimePreferBoshMinWsFailures);
        const minFailures = Number.isFinite(configuredMin) && configuredMin > 0
            ? configuredMin
            : DEFAULT_RUNTIME_PREFER_BOSH_MIN_WS_FAILURES;
        const currentCount = Number(window.sessionStorage?.getItem(RUNTIME_WS_FAILURE_COUNT_KEY) || '0');
        const nextCount = Number.isFinite(currentCount) ? currentCount + 1 : 1;

        window.sessionStorage?.setItem(RUNTIME_WS_FAILURE_COUNT_KEY, String(nextCount));

        // A single failure only earns a one-shot BOSH retry for the current attempt (the caller handles that).
        // The session-wide preference is deliberately left untouched so a slow-to-establish websocket on a
        // high-latency link does not pin the user to BOSH for the rest of the meeting.
        if (nextCount < minFailures) {
            logger?.warn(
                `WebSocket failed (${nextCount}/${minFailures}); retrying this attempt over BOSH, `
                    + 'session transport preference left unchanged.');

            return;
        }

        window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SESSION_KEY, '1');
        window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SET_AT_KEY, String(Date.now()));
        logger?.warn(`WebSocket failed ${nextCount} times; preferring BOSH for subsequent connect attempts.`);

        if (nextCount >= maxFailures) {
            window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_LOCK_KEY, '1');
            logger?.warn(`WebSocket instability threshold reached (${nextCount}/${maxFailures}); forcing BOSH for this session.`);
        }
    } catch (error) {
        logger?.warn('Failed to persist websocket failure counter.', error);
    }
}

/**
 * Forces runtime BOSH preference for the current session.
 *
 * @param {Object} config - The global config object.
 * @param {Object} logger - Logger instance.
 * @param {Object} [options] - Additional options.
 * @param {boolean} [options.incrementFailureCount=true] - Whether to increment the websocket failure counter.
 * @param {string} [options.reason='runtime-failover'] - Human-readable reason used for logs.
 * @returns {void}
 */
export function forceRuntimePreferBosh(config, logger, options = {}) {
    const {
        incrementFailureCount = true,
        reason = 'runtime-failover'
    } = options;

    if (incrementFailureCount) {
        recordWebSocketFailure(config, logger);
        logger?.warn(`Runtime BOSH preference enabled for session (${reason}).`);

        return;
    }

    try {
        window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SESSION_KEY, '1');
        window.sessionStorage?.setItem(RUNTIME_PREFER_BOSH_SET_AT_KEY, String(Date.now()));
        logger?.warn(`Runtime BOSH preference enabled for session (${reason}).`);
    } catch (error) {
        logger?.warn('Failed to persist runtime BOSH preference.', error);
    }
}
